import { z } from 'zod';
import { useMemo } from 'react';
import { CONTACT_FORM_DEFAULT_VALUES, contactSchema } from '@lp/features/admin/forms/MainContactForm';
import { contactSchemaOptional } from '@features/forms/MainContactForm';
import { TMDXFile } from '@/api/apis/files';
import { NameByValue } from '@/types';
import { STATE_SCHEMA } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { createHospitalFormValidation } from './utils';
import { NewHospitalForm } from '@/types/facility.types';

export const DEFAULT_HOSPITAL_VALUES: NewHospitalForm = {
  name: '',
  code: '',
  timezone: '',
  timezoneIANA: '',
  addressId: '',
  addressLine: '',
  city: '',
  state: null,
  postalCode: '',
  country: '',
  longitude: 0,
  latitude: 0,
  ...CONTACT_FORM_DEFAULT_VALUES,
  primaryContactId: null,
};

export const hospitalSchema = z.object({
  name: z.string().min(1, 'Provide donor hospital name'),
  code: z.string(),
  image: z.custom<TMDXFile>().optional(),
  logoPath: z.string().optional(),
  timezone: z.string(),
  timezoneIANA: z.string(),
  addressId: z.string().optional(),
  addressLine: z.string().min(1, 'Provide donor hospital address'),
  city: z.string().min(1, 'Provide donor hospital city'),
  state: STATE_SCHEMA,
  postalCode: z.string().min(1, 'Provide donor hospital postal code'),
  country: z.string(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  primaryContactId: z.string().nullable().optional(),
});

export const hospitalFormSchemaRequiredContact = (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  hospitalSchema.extend(contactSchema.shape).superRefine(createHospitalFormValidation(composedSubdivisionsList));

export const hospitalFormSchemaOptionalContact = (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  hospitalSchema.extend(contactSchemaOptional).superRefine(createHospitalFormValidation(composedSubdivisionsList));

export const hospitalFormSchema = (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  hospitalSchema.extend(contactSchema.shape).superRefine(createHospitalFormValidation(composedSubdivisionsList));

export const useHospitalFormSchemaOptionalContact = () => {
  const { composedSubdivisionsList, isLoading } = useCountriesData();
  const schema = useMemo(() => hospitalFormSchemaOptionalContact(composedSubdivisionsList), [composedSubdivisionsList]);
  return { schema, isLoading };
};
